// components/ui/tooltip.tsx
'use client';

import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '#lib/utils';

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

/** Where hover labels mount: the page's body, or the body of a window of the app's own its tree renders into (components/fater3d/ViewWindow.tsx). */
const TooltipContainer = React.createContext<HTMLElement | null>(null);

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, side, style, ...props }, ref) => (
  <TooltipPrimitive.Portal container={React.useContext(TooltipContainer) ?? undefined}>
    {/* ChatGPT's hover label (owner, 2026-09-28): a small dark pill, a word or two in medium white,
        then the keyboard shortcut in a key chip (TooltipKey) when there is one, never a sentence. */}
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      side={side} // Pass the side prop down
      className={cn(
        'flex items-center gap-1.5 bg-[#2B2B2B] font-medium text-white animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 overflow-hidden rounded-[10px] px-2.5 py-1 shadow-[0_8px_24px_rgba(0,0,0,0.45)]',
        className,
        'data-[side=right]:translate-y-[-8px]'
      )}
      style={{ fontSize: 13, ...style }}
      {...props}
    />
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

/** A keyboard shortcut inside a hover label, drawn as ChatGPT's key chip. */
const TooltipKey = ({ children }: { children: React.ReactNode }) => (
  <kbd className="rounded-[5px] bg-white/[0.12] px-1.5 font-sans font-medium text-[#CDCDCD]" style={{ fontSize: 12, lineHeight: '18px' }}>
    {children}
  </kbd>
);

interface AppTooltipProps {
  content: React.ReactNode;
  children: React.ReactElement;
  side?: React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>['side'];
  sideOffset?: number;
  contentClassName?: string;
  disabled?: boolean;
}

const AppTooltip = React.memo(({
  content,
  children,
  side = 'top',
  sideOffset = 6,
  contentClassName,
  disabled = false,
}: AppTooltipProps) => {
  if (disabled || !content) {
    return children;
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side} sideOffset={sideOffset} className={contentClassName}>
        {content}
      </TooltipContent>
    </Tooltip>
  );
});
AppTooltip.displayName = 'AppTooltip';

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, TooltipContainer, AppTooltip, TooltipKey };
